import React from "react";
import { IDataTableV2State } from "../../hooks/useTableV2State";
import { DataTableV2DateTypes } from "../../preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputBase,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import { DateFilterDropdown } from "../../../Table/components/DateFilterDropdown/DateFilterDropdown";
import { dateDropdownList } from "../../preDefinedPlugins/DataTableV2Date/DataTableV2Date";
import produce from "immer";
import { getDataTablev2InitialDate } from "../../helpers/dataTableV2DatesFilter";
import { CustomDatePicker } from "../../../formsComponents/CustomDatePicker";
import { CustomFormLabel } from "../../../formsComponents";
import { RightPanel } from "../../../RightPanel";
import { Refresh } from "@mui/icons-material";

export const MobileFilterContext = React.createContext<{
  closeFilter: () => void;
}>({
  closeFilter: () => {},
});

export const useMobileFilter = () => React.useContext(MobileFilterContext);

interface IDataTableV2MobileFilterProps {
  state: IDataTableV2State;
  onApply: (newState: IDataTableV2State) => void;
  onClear: () => void;
  onRefresh?: () => void;
  searchFilterNode?: React.ReactNode;
  inlineActions?: React.ReactNode;
  extraActions?: React.ReactNode;
  children?: (
    filterLocalState: IDataTableV2State,
    onFilterlocalStateChange: (newfilterLocalState: IDataTableV2State) => void,
  ) => React.ReactNode;
  enableDateFilter?: boolean;
  initialExpanded?: boolean;
  filterButtonLabel?: string;
  panelHeading?: React.ReactNode;
  hasFilterApplied?: boolean;
  pageTitle?: string;
  headerAction?: React.ReactNode;
  searchValue?: string;
  onSearchChange?: (val: string) => void;
  searchPlaceholder?: string;
}

export const DataTableV2MobileFilter: React.FC<
  IDataTableV2MobileFilterProps
> = (props) => {
  const {
    state,
    enableDateFilter,
    onApply,
    children,
    onClear,
    onRefresh,
    searchFilterNode,
    inlineActions,
    extraActions,
    filterButtonLabel,
    panelHeading,
    hasFilterApplied,
    initialExpanded = false,
    pageTitle,
    headerAction,
    searchValue,
    onSearchChange,
    searchPlaceholder,
  } = props;

  const [openDrawer, setOpenDrawer] = React.useState(initialExpanded);
  const theme = useTheme();
  const [localState, setLocalState] = React.useState(state);

  const handleDateRangeSelectChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as DataTableV2DateTypes;

    if (value === "custom") {
      const newLocalState = produce(localState, (draftState) => {
        draftState.dateState.rangeType = value;
      });

      setLocalState(newLocalState);
      return;
    }
    const dates = getDataTablev2InitialDate(value);

    const newLocalState = produce(localState, (draftState) => {
      draftState.dateState.rangeType = value;
      draftState.dateState.dates = {
        fromDate: dates.fromDate,
        toDate: dates.toDate,
      };
    });
    setLocalState(newLocalState);
  };

  const handleCustomDate = (key: string) => (newValue: any) => {
    const newLocalState = produce(localState, (draftState) => {
      draftState.dateState.dates[key as "fromDate"] = newValue;
    });
    setLocalState(newLocalState);
  };

  const handleApply = () => {
    onApply(localState);
    setOpenDrawer(false);
  };

  const handleClear = () => {
    onClear();
    setOpenDrawer(false);
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      onApply({ ...localState });
    }
  };

  React.useEffect(() => {
    setLocalState(state);
  }, [state]);

  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (state.otherCustomStateProperties) {
      count += Object.values(state.otherCustomStateProperties).filter((v) => {
        if (v === "" || v === null || v === undefined || v === false)
          return false;
        if (Array.isArray(v) && v.length === 0) return false;
        if (typeof v === "object" && !Array.isArray(v) && v !== null) {
          return Object.values(v).some(
            (val) =>
              val !== "" && val !== null && val !== undefined && val !== false,
          );
        }
        return true;
      }).length;
    }
    if (state.searchState && state.searchState.length > 0) {
      count += state.searchState.length;
    }
    if (
      state.tabs?.selectedTab &&
      state.tabs.selectedTab !== "-1" &&
      state.tabs.selectedTab !== "0"
    ) {
      count += 1;
    }
    if (
      state.dateState?.dates?.fromDate &&
      state.dateState?.rangeType !== "allTimes"
    ) {
      count += 1;
    }
    return count;
  }, [state]);

  const isFiltered =
    hasFilterApplied !== undefined ? hasFilterApplied : activeFilterCount > 0;

  const currentSearchText =
    searchValue !== undefined
      ? searchValue
      : localState.otherCustomStateProperties?.searchByNameOrNumber || "";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          py: 0.5,
          px: 0.25,
          mb: 1,
        }}
      >
        {/* 1st Row: Page Title Heading on Left, Action Button / Stack on Right */}
        {(pageTitle || headerAction || extraActions) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 0.5,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: "#0f172a",
                fontSize: { xs: "1.35rem", sm: "1.6rem" },
              }}
            >
              {pageTitle ||
                (typeof panelHeading === "string" ? panelHeading : "Leads")}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {inlineActions}
              {headerAction}
              {onRefresh && (
                <IconButton
                  size="small"
                  onClick={handleRefresh}
                  sx={{
                    backgroundColor: "#f1f5f9",
                    color: "#475569",
                    width: 36,
                    height: 36,
                    "&:hover": { backgroundColor: "#e2e8f0" },
                  }}
                >
                  <Refresh sx={{ fontSize: "20px" }} />
                </IconButton>
              )}
            </Box>
          </Box>
        )}

        {/* 2nd Row: PhonePe Style Pill Search Bar with Filter Icon on Right */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "#f4f3f8",
            borderRadius: "30px",
            px: 2.25,
            py: 0.65,
            my: 0.75,
            transition: "all 0.2s ease",
            "&:focus-within": {
              bgcolor: "#ffffff",
              boxShadow: "0 0 0 2px rgba(2, 132, 199, 0.2)",
            },
          }}
        >
          <SearchIcon sx={{ color: "#64748b", mr: 1, fontSize: "22px" }} />
          <InputBase
            placeholder={searchPlaceholder || "Search transactions or leads..."}
            value={currentSearchText}
            onChange={(e) => {
              const val = e.target.value;
              if (onSearchChange) {
                onSearchChange(val);
              } else {
                const newLocal = produce(localState, (draft) => {
                  if (!draft.otherCustomStateProperties) {
                    draft.otherCustomStateProperties = {};
                  }
                  draft.otherCustomStateProperties.searchByNameOrNumber = val;
                });
                setLocalState(newLocal);
                onApply(newLocal);
              }
            }}
            sx={{
              flex: 1,
              fontSize: "0.9rem",
              fontWeight: 500,
              color: "#0f172a",
              "& input::placeholder": {
                color: "#94a3b8",
                opacity: 1,
              },
            }}
          />

          {isFiltered && (
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{ color: "#ef4444", p: 0.25, mr: 0.5 }}
              title="Clear Filters"
            >
              <FilterAltOffIcon sx={{ fontSize: "18px" }} />
            </IconButton>
          )}

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.75, my: 0.5, borderColor: "#cbd5e1" }}
          />

          <IconButton
            onClick={() => setOpenDrawer(true)}
            sx={{
              p: 0.5,
              color: activeFilterCount > 0 ? "#0284c7" : "#475569",
              position: "relative",
            }}
            title={filterButtonLabel || "Filters"}
          >
            <TuneIcon sx={{ fontSize: "22px" }} />
            {activeFilterCount > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "#0284c7",
                }}
              />
            )}
          </IconButton>
        </Box>

        {/* Extra Action Buttons below if any */}
        {extraActions && !headerAction && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              width: "100%",
              overflowX: "auto",
              pb: 0.25,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {extraActions}
          </Box>
        )}
      </Box>

      {/* RightPanel Drawer for Advanced Filters */}
      <MobileFilterContext.Provider
        value={{ closeFilter: () => setOpenDrawer(false) }}
      >
        <RightPanel
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          heading={typeof panelHeading === "string" ? panelHeading : "Filters"}
          customHeading={
            typeof panelHeading !== "string" ? panelHeading : undefined
          }
          actionButtons={() => (
            <Stack direction="row" spacing={1.5} sx={{ width: "100%", py: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClear}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1,
                  fontSize: "0.875rem",
                  borderColor: "#cbd5e1",
                  color: "#475569",
                  "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
                }}
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleApply}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1,
                  fontSize: "0.875rem",
                  bgcolor: "#0284c7",
                  boxShadow: "0 4px 12px rgba(2, 132, 199, 0.25)",
                  "&:hover": { bgcolor: "#0369a1" },
                }}
              >
                Apply Filters
              </Button>
            </Stack>
          )}
        >
          <Stack spacing={2} sx={{ py: 1 }}>
            {enableDateFilter && (
              <Box>
                <CustomFormLabel>Date</CustomFormLabel>
                <DateFilterDropdown
                  value={localState.dateState.rangeType}
                  options={dateDropdownList}
                  onChange={handleDateRangeSelectChange}
                  sx={{ width: "100%" }}
                />
                {localState.dateState.rangeType === "custom" && (
                  <Grid container spacing={1.5} mt={0.5}>
                    <Grid item xs={12}>
                      <CustomFormLabel sx={{ mt: 0 }}>
                        From Date
                      </CustomFormLabel>
                      <CustomDatePicker
                        fullWidth
                        value={localState.dateState.dates.fromDate}
                        onChange={handleCustomDate("fromDate")}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CustomFormLabel sx={{ mt: 0 }}>To Date</CustomFormLabel>
                      <CustomDatePicker
                        fullWidth
                        value={localState.dateState.dates.toDate}
                        onChange={handleCustomDate("toDate")}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}

            {children && children(localState, setLocalState)}

            {searchFilterNode && (
              <Box
                sx={{
                  mt: 1,
                  pt: 1.5,
                  borderTop: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Advanced Search Criteria
                </Typography>
                {searchFilterNode}
              </Box>
            )}
          </Stack>
        </RightPanel>
      </MobileFilterContext.Provider>
    </>
  );
};
